import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Handshake, CheckCircle2, Trophy } from "lucide-react";
import { toast } from "sonner";

export default function PartnerApply() {
  const [form, setForm] = useState({ name: "", email: "", channel: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [leaders, setLeaders] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/partners/leaderboard");
        setLeaders(data.leaders || []);
      } catch { /* leaderboard optional */ }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.channel) { toast.error("All fields are required"); return; }
    setBusy(true);
    try {
      await api.post("/partners/apply", form);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit application");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[70vh] max-w-2xl mx-auto px-6 py-16" data-testid="partner-apply-page">
      <div className="flex items-center gap-2 text-[#34C759]">
        <Handshake className="h-5 w-5" />
        <span className="text-xs uppercase tracking-widest">Affiliate program</span>
      </div>
      <h1 className="mt-3 font-black tracking-tight text-4xl sm:text-5xl">
        Earn for every seller <span className="text-[#FF3B30]">you bring</span>
      </h1>
      <p className="text-neutral-400 mt-4">
        Promote VISMARKS with your unique tracked link — blog, channel, community or audience.
        Every signup you drive earns you cash credit, tracked automatically.
      </p>

      {done ? (
        <div className="mt-10 rounded-xl border border-[#34C759]/40 bg-[#34C759]/10 p-8 text-center" data-testid="partner-apply-success">
          <CheckCircle2 className="h-10 w-10 text-[#34C759] mx-auto" />
          <div className="mt-4 font-black text-xl">Application received</div>
          <p className="text-neutral-400 text-sm mt-2">
            We review every application. Once approved, your unique tracked link lands in your inbox.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-10 space-y-4 rounded-xl border border-[#27272A] bg-[#121212] p-8">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Your name or brand" data-testid="apply-name-input"
            className="w-full h-11 rounded-md bg-[#0A0A0A] border border-[#27272A] px-3 text-sm text-white focus:outline-none focus:border-[#34C759]" />
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="Email — we send your link here" data-testid="apply-email-input"
            className="w-full h-11 rounded-md bg-[#0A0A0A] border border-[#27272A] px-3 text-sm text-white focus:outline-none focus:border-[#34C759]" />
          <textarea value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })}
            placeholder="Where will you promote VISMARKS? (Telegram channel, X account, blog, community…)" rows={3} maxLength={300}
            data-testid="apply-channel-input"
            className="w-full rounded-md bg-[#0A0A0A] border border-[#27272A] px-3 py-2 text-sm text-white focus:outline-none focus:border-[#34C759]" />
          <button type="submit" disabled={busy} data-testid="apply-submit-btn"
            className="w-full h-11 rounded-full bg-[#34C759] text-black font-bold text-sm hover:bg-[#2eb350] transition-colors disabled:opacity-50">
            {busy ? "Submitting…" : "Apply for your tracked link"}
          </button>
        </form>
      )}

      {leaders.length > 0 && (
        <div className="mt-12" data-testid="partner-leaderboard">
          <div className="flex items-center gap-2">
            <Trophy className="h-4 w-4 text-[#FF9F0A]" />
            <h2 className="font-black text-xl tracking-tight">Top partners</h2>
          </div>
          <ol className="mt-4 space-y-2">
            {leaders.map((l, i) => (
              <li key={i} className="flex items-center gap-4 rounded-lg border border-[#27272A] bg-[#121212] px-4 py-3" data-testid={`leaderboard-row-${i}`}>
                <span className={`font-mono font-black text-lg w-8 ${i === 0 ? "text-[#FF9F0A]" : i === 1 ? "text-neutral-300" : i === 2 ? "text-[#b87333]" : "text-neutral-500"}`}>
                  #{i + 1}
                </span>
                <span className="flex-1 text-white font-medium truncate">{l.name}</span>
                <span className="font-mono text-sm text-neutral-400">{l.clicks} clicks</span>
                <span className="font-mono text-sm text-[#34C759]">{l.signups} signups</span>
              </li>
            ))}
          </ol>
          <p className="text-xs text-neutral-500 mt-3">Rankings update live from tracked link performance.</p>
        </div>
      )}
    </div>
  );
}
